// @ts-nocheck

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import styled, { css, keyframes } from 'styled-components';

import SpinePlayer from '../../../components/SpinePlayer';
import useDelayedPropVariation from '../../../hooks/useDelayedPropVariation';
import { __ } from '../../../i18n/l10n';
import { GRLocker, GRRoundWonRibbonImgPath } from '../assets/img';
import {
  GRRound1BackgroundImgPath,
  GRRound1BackgroundFallbackImgPath,
  GRRound2BackgroundImgPath,
  GRRound2BackgroundFallbackImgPath,
  GRRound3BackgroundImgPath,
  GRRound3BackgroundFallbackImgPath,
} from '../assets/img/backgrounds';
import { GRSpineAnimationIdByRoundId } from '../constants/order';
import GRChipDetailsContainer from '../containers/GRChipDetailsContainer';
import GRChipListContainer from '../containers/GRChipListContainer';
import GRProgressBarContainer from '../containers/GRProgressBarContainer';
import GRRoundWonTagContainer from '../containers/GRRoundWonTagContainer';
import GRRulesButtonContainer from '../containers/GRRulesButtonContainer';
import { GRRoundContext } from '../contexts/GRRoundContext';
import useGRHomeContext from '../hooks/useGRHomeContext';
import { GRRounds, type GRRound as GRRoundType } from '../types/round';

const LockedBackgroundPathByRound = {
  [GRRounds.SHOE]: GRRound1BackgroundFallbackImgPath,
  [GRRounds.BALLOON]: GRRound2BackgroundFallbackImgPath,
  [GRRounds.SHIRT]: GRRound3BackgroundFallbackImgPath,
};

const UnlockedBackgroundPathByRound = {
  [GRRounds.SHOE]: GRRound1BackgroundImgPath,
  [GRRounds.BALLOON]: GRRound2BackgroundImgPath,
  [GRRounds.SHIRT]: GRRound3BackgroundImgPath,
};

const sharedBackgroundsStyle = css`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  z-index: -1;

  ${({ $backgroundPath }) =>
    $backgroundPath
      ? css`
          background-image: url(${$backgroundPath});
          background-size: cover;
          background-repeat: no-repeat;
          background-position: center;
        `
      : undefined}
`;

const UnlockedBackground = styled.div`
  ${sharedBackgroundsStyle}

  ${({ $isLocked }) => css`
    opacity: ${$isLocked ? 0 : 1};
  `}
`;

const LockedBackground = styled.div`
  ${sharedBackgroundsStyle}

  ${({ $isLocked }) => css`
    opacity: ${$isLocked ? 1 : 0};
  `}
`;

const appearAnim = keyframes`
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
`;

const Locker = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);

  background-image: url(${GRLocker});
  background-repeat: no-repeat;
  background-size: contain;
  width: 7em;
  height: 7em;

  &.locker-transition-enter {
    animation: ${appearAnim} 300ms ease-in-out forwards;
  }

  &.locker-transition-exit {
    animation: ${appearAnim} 300ms ease-in-out forwards reverse;
  }
`;

const GRChipDetailsWrapper = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(0deg, #000000ff, #000000aa, #00000000);
  opacity: 0;
  padding: 1em;

  display: flex;
  align-items: flex-end;
  justify-content: center;

  transition: none 300ms ease-in-out;
  transition-property: transform, opacity;
  padding-bottom: 14%;

  ${({ $show }) => {
    if ($show) {
      return css`
        transform: translateY(-100%);
        opacity: 1;
      `;
    }
  }};
`;

const animGrayscale = keyframes`
  from {
    filter: grayscale(1);
    pointer-events: none;
  }

  to {
    filter: grayscale(0);
    pointer-events: auto;
  }
`;

const Wrapper = styled.div`
  position: relative;
  padding: 1em;
  transform: translateZ(0);
  overflow: hidden;
  height: 100%;
  max-height: 100%;

  & > *:not(${Locker}):not(${GRChipDetailsWrapper}) {
    transition: filter 1000ms ease-in-out;
  }

  ${({ $wasPreviouslyLocked, $isLocked }) =>
    $isLocked
      ? css`
          & > *:not(${Locker}):not(${GRChipDetailsWrapper}) {
            filter: grayscale(1);
          }
          pointer-events: none;
        `
      : css`
          & > *:not(${Locker}):not(${GRChipDetailsWrapper}) {
            ${$wasPreviouslyLocked
              ? css`
                  animation: ${animGrayscale} 1000ms ease-in-out forwards;
                `
              : undefined}
          }
        `}
`;

const LeftColumn = styled.div``;

const RightColumn = styled.div`
  position: absolute;
  right: 0;
  top: 1%;
  bottom: 0;
  width: 20%;
  height: 99%;

  display: flex;
  flex-direction: column;
  align-items: center;
`;

const StyledRulesBtn = styled(GRRulesButtonContainer)`
  font-size: 135%;
`;

const StyledGRChipDetailsContainer = styled(GRChipDetailsContainer)`
  &.chip-details-transition-enter {
    animation: ${appearAnim} 300ms ease-in-out forwards;
  }

  &.chip-details-transition-exit {
    animation: ${appearAnim} 300ms ease-in-out forwards reverse;
  }
`;

const spinePositioningByRoundId = {
  [GRRounds.SHOE]: css`
    left: -2.5em;
    top: 7.5em;
    transform: scale(2);
  `,
  [GRRounds.BALLOON]: css`
    left: 0em;
    top: 55%;
    transform: translateY(-50%) scale(2.5);
  `,
  [GRRounds.SHIRT]: css`
    left: 0em;
    top: 50%;
    transform: translateY(-50%) scale(1.25);
  `,
};

const StyledSpinePlayer = styled(SpinePlayer)`
  position: absolute;

  ${({ $roundId }) => spinePositioningByRoundId[$roundId]}
`;

const StyledGRProgressBarContainer = styled(GRProgressBarContainer)`
  position: absolute;
  bottom: 3em;
  left: 40%;
  transform: translateX(-50%);
  width: 75%;
`;

const ribbonImgRatio = 1.41136363636;
const ribbonWidth = 50; // %
const ribbonWidthFallback = 20; // em
const WonRibbon = styled.div`
  position: absolute;
  left: 0;
  top: -1.2em;
  width: min(${ribbonWidth}%, ${ribbonWidthFallback}em);
  padding-bottom: min(${ribbonImgRatio * ribbonWidth}%, ${ribbonImgRatio * ribbonWidthFallback}em);
  background-image: url(${GRRoundWonRibbonImgPath});
  background-size: contain;
  background-repeat: no-repeat;

  &:after {
    content: '${__('!noun:Won')}';
    position: absolute;
    color: white;
    bottom: 19%;
    left: 8%;
    transform: rotate(-52deg);
    transform-origin: left center;
    font-size: 1.9em;
    font-weight: bold;
    width: 5em;
    text-align: center;
  }
`;

const StyledGRRoundWonTagContainer = styled(GRRoundWonTagContainer)`
  position: absolute;
  bottom: 3em;
  left: 55%;
  transform: translateX(-50%);
`;

type GRRoundProps = {
  className?: string;
  roundId: GRRoundType;
  children?: React.ReactNode;
  isLocked: boolean;
  isWon: boolean;
};

const GRRound = ({ className, roundId, isLocked, isWon, children }: GRRoundProps) => {
  const [delayedIsLocked, prevDelayedIsLocked] = useDelayedPropVariation(isLocked);
  const [lastSelectedChipIndex, setLastSelectedChipIndex] = useState(null);
  const [chipDetailsPanelIsOpened, setChipDetailsPanelIsOpened] = useState(false);
  const { fullScreenNotifShown } = useGRHomeContext();

  const onChipClick = useCallback((index) => {
    setLastSelectedChipIndex(index);
    setChipDetailsPanelIsOpened(true);
  }, []);

  const contextState = useMemo(
    () => ({
      roundId,
      isLocked,
      isWon,
      onChipClick,
      lastSelectedChipIndex,
      chipDetailsPanelIsOpened,
    }),
    [chipDetailsPanelIsOpened, lastSelectedChipIndex, isWon, onChipClick, roundId, isLocked],
  );

  const onGlobalClick = useCallback(() => {
    setChipDetailsPanelIsOpened(false);
  }, []);

  useEffect(() => {
    if (fullScreenNotifShown) {
      onGlobalClick();
    }
  }, [fullScreenNotifShown, onGlobalClick]);

  const lockedBackgroundPath = useMemo(() => LockedBackgroundPathByRound[roundId], [roundId]);
  const unlockedBackgroundPath = useMemo(() => UnlockedBackgroundPathByRound[roundId], [roundId]);

  return (
    <GRRoundContext.Provider value={contextState}>
      <Wrapper
        className={className}
        onClick={onGlobalClick}
        $wasPreviouslyLocked={prevDelayedIsLocked}
        $isLocked={delayedIsLocked}
      >
        <LockedBackground $backgroundPath={lockedBackgroundPath} $isLocked={delayedIsLocked} />
        <UnlockedBackground $backgroundPath={unlockedBackgroundPath} $isLocked={delayedIsLocked} />
        {isWon ? <WonRibbon /> : null}
        {!delayedIsLocked ? (
          <StyledSpinePlayer
            $roundId={roundId}
            animationId={GRSpineAnimationIdByRoundId[roundId]}
            width={1500}
            height={1500}
            hideMuteButton
          />
        ) : null}
        {!isWon ? (
          <LeftColumn>
            <StyledRulesBtn />
          </LeftColumn>
        ) : null}
        <RightColumn>
          <GRChipListContainer />
        </RightColumn>
        <StyledGRProgressBarContainer />
        <StyledGRRoundWonTagContainer />
        <GRChipDetailsWrapper $show={chipDetailsPanelIsOpened}>
          <CSSTransition
            classNames={'chip-details-transition'}
            in={chipDetailsPanelIsOpened}
            timeout={300}
            unmountOnExit
            mountOnEnter
          >
            <StyledGRChipDetailsContainer />
          </CSSTransition>
        </GRChipDetailsWrapper>
        <CSSTransition classNames={'locker-transition'} in={delayedIsLocked} timeout={1000} unmountOnExit mountOnEnter>
          <Locker />
        </CSSTransition>
        {children}
      </Wrapper>
    </GRRoundContext.Provider>
  );
};

export default GRRound;
